import React, { useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormLabel,
  LinearProgress,
  Paper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { CheckCircle, HourglassEmpty } from "@mui/icons-material";
import { useSearchParams } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/client";
import { ThinLayout } from "../Layouts/ThinLayout";
import {
  GET_MATCHING_SURVEY,
  SUBMIT_MATCHING_RESPONSE,
  MatchingSurveyResult,
  SubmitMatchingResponseResult,
} from "../Queries";
import { MatchingQuestion, MatchingQuestionType, RosterMember } from "../types";

// ── Time-slot grid ────────────────────────────────────────────────────────────

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const SLOTS = ["Morning", "Afternoon", "Evening"];

function TimeAvailabilityInput({
  value,
  onChange,
}: {
  value: string[];
  onChange: (v: string[]) => void;
}) {
  const toggle = (slot: string) => {
    if (value.includes(slot)) {
      onChange(value.filter((s) => s !== slot));
    } else {
      onChange([...value, slot]);
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: `repeat(${DAYS.length}, 1fr)`,
          gap: 0.5,
        }}
      >
        {DAYS.map((day) => (
          <Typography
            key={day}
            variant="caption"
            align="center"
            sx={{ fontWeight: 600 }}
          >
            {day}
          </Typography>
        ))}
        {SLOTS.map((slot) =>
          DAYS.map((day) => {
            const key = `${day}:${slot}`;
            const selected = value.includes(key);
            return (
              <Chip
                key={key}
                label={slot.slice(0, 3)}
                size="small"
                color={selected ? "primary" : "default"}
                onClick={() => toggle(key)}
                sx={{ cursor: "pointer", fontSize: "0.65rem" }}
              />
            );
          }),
        )}
      </Box>
    </Box>
  );
}

// ── Role preference ───────────────────────────────────────────────────────────

const DEFAULT_ROLES = [
  "Project Manager",
  "Designer",
  "Developer",
  "Researcher",
  "Presenter",
];

function RolePreferenceInput({
  options,
  value,
  onChange,
}: {
  options: string[];
  value: string;
  onChange: (v: string) => void;
}) {
  const roles = options.length > 0 ? options : DEFAULT_ROLES;
  return (
    <ToggleButtonGroup
      value={value}
      exclusive
      onChange={(_e, v) => v && onChange(v)}
      sx={{ flexWrap: "wrap", gap: 0.5 }}
    >
      {roles.map((role) => (
        <ToggleButton key={role} value={role} sx={{ textTransform: "none" }}>
          {role}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}

// ── Personality ───────────────────────────────────────────────────────────────

const BIG_FIVE = [
  {
    value: "Openness",
    description: "creative, curious, open to new experiences and ideas",
  },
  {
    value: "Conscientiousness",
    description: "organized, disciplined, detail-oriented and reliable",
  },
  {
    value: "Extraversion",
    description: "outgoing, energetic, seeks social interaction",
  },
  {
    value: "Agreeableness",
    description: "cooperative, trusting, empathetic and helpful",
  },
  {
    value: "Emotional stability",
    description: "calm, resilient and handles stress well",
  },
];

function PersonalityInput({
  options,
  value,
  onChange,
}: {
  options: string[];
  value: string;
  onChange: (v: string) => void;
}) {
  // If the instructor has customised options, fall back to plain toggle buttons.
  if (options.length > 0) {
    return (
      <ToggleButtonGroup
        value={value}
        exclusive
        onChange={(_e, v) => v && onChange(v)}
        sx={{ flexWrap: "wrap", gap: 0.5 }}
      >
        {options.map((t) => (
          <ToggleButton
            key={t}
            value={t}
            sx={{ textTransform: "none", fontSize: "0.75rem" }}
          >
            {t}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    );
  }

  return (
    <ToggleButtonGroup
      value={value}
      exclusive
      onChange={(_e, v) => v && onChange(v)}
      sx={{ flexDirection: "column", gap: 0.5, alignItems: "stretch" }}
    >
      {BIG_FIVE.map(({ value: trait, description }) => (
        <ToggleButton
          key={trait}
          value={trait}
          sx={{
            textTransform: "none",
            justifyContent: "flex-start",
            textAlign: "left",
            py: 1.5,
            px: 2,
          }}
        >
          <Box>
            <Typography variant="body2" fontWeight={600}>
              {trait}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {description}
            </Typography>
          </Box>
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}

// ── Teammate chip selector (roster-backed autocomplete) ───────────────────────

function TeammateChipSelector({
  roster,
  excludedIds,
  value,
  onChange,
}: {
  roster: RosterMember[];
  excludedIds: string[];
  value: string[];
  onChange: (ids: string[]) => void;
}) {
  const selectedMembers = roster.filter((m) => value.includes(m.id));
  const options = roster.filter((m) => !value.includes(m.id));

  return (
    <Autocomplete
      multiple
      options={options}
      getOptionLabel={(m) => m.name || m.email}
      getOptionDisabled={(m) => excludedIds.includes(m.id)}
      value={selectedMembers}
      onChange={(_e, newValue) => onChange(newValue.map((m) => m.id))}
      isOptionEqualToValue={(opt, val) => opt.id === val.id}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={roster.length > 0 ? "Search classmates…" : ""}
          size="small"
        />
      )}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          {option.name || option.email}
          {excludedIds.includes(option.id) && (
            <Typography variant="caption" color="error" ml={1}>
              (conflict)
            </Typography>
          )}
        </li>
      )}
    />
  );
}

// ── Per-question renderer ─────────────────────────────────────────────────────

type AnswerMap = Record<string, unknown>;

function QuestionCard({
  question,
  answer,
  onAnswer,
  roster,
  excludedIds,
}: {
  question: MatchingQuestion;
  answer: unknown;
  onAnswer: (v: unknown) => void;
  roster: RosterMember[];
  excludedIds: string[];
}) {
  const options = Array.isArray(question.options)
    ? (question.options as string[])
    : [];

  let input: React.ReactNode;

  switch (question.questionType as MatchingQuestionType) {
    case MatchingQuestionType.TimeAvailability:
      input = (
        <TimeAvailabilityInput
          value={(answer as string[]) ?? []}
          onChange={onAnswer}
        />
      );
      break;

    case MatchingQuestionType.RolePreference:
      input = (
        <RolePreferenceInput
          options={options}
          value={(answer as string) ?? ""}
          onChange={onAnswer}
        />
      );
      break;

    case MatchingQuestionType.Personality:
      input = (
        <PersonalityInput
          options={options}
          value={(answer as string) ?? ""}
          onChange={onAnswer}
        />
      );
      break;

    case MatchingQuestionType.PreferredTeammates:
    case MatchingQuestionType.AvoidTeammates:
      input = (
        <TeammateChipSelector
          roster={roster}
          excludedIds={excludedIds}
          value={(answer as string[]) ?? []}
          onChange={onAnswer}
        />
      );
      break;

    // Gender renders as a single-select from the instructor-defined options,
    // identical to a custom single-select question.
    case MatchingQuestionType.Gender:
    case MatchingQuestionType.CustomText:
    default:
      if (options.length > 0) {
        input = (
          <ToggleButtonGroup
            value={answer ?? ""}
            exclusive
            onChange={(_e, v) => v && onAnswer(v)}
            sx={{ flexWrap: "wrap", gap: 0.5 }}
          >
            {options.map((opt) => (
              <ToggleButton
                key={opt}
                value={opt}
                sx={{ textTransform: "none" }}
              >
                {opt}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        );
      } else {
        input = (
          <TextField
            fullWidth
            multiline
            rows={3}
            value={(answer as string) ?? ""}
            onChange={(e) => onAnswer(e.target.value)}
            placeholder="Your answer…"
          />
        );
      }
      break;
  }

  return (
    <Paper elevation={1} sx={{ p: 3, mb: 2 }}>
      <FormLabel
        sx={{ display: "block", mb: 0.5, fontWeight: 600, fontSize: "1rem" }}
      >
        {question.label}
        {question.required && (
          <Typography component="span" color="error" ml={0.5}>
            *
          </Typography>
        )}
      </FormLabel>
      {question.description && (
        <Typography variant="body2" color="text.secondary" mb={1.5}>
          {question.description}
        </Typography>
      )}
      {input}
    </Paper>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function MatchingSurveyPage() {
  const [searchParams] = useSearchParams();
  const key = searchParams.get("key") ?? "";

  // Decode token from key (same pattern as UserResponse)
  const keyBlob = (() => {
    try {
      return atob(key);
    } catch {
      return "";
    }
  })();
  const jwtToken = keyBlob.split(":").slice(1).join(":");

  const authHeaders = jwtToken ? { Authorization: `JWT ${jwtToken}` } : {};

  const [answers, setAnswers] = useState<AnswerMap>({});
  const [submitted, setSubmitted] = useState(false);

  const { loading, data, error } = useQuery<MatchingSurveyResult>(
    GET_MATCHING_SURVEY,
    {
      variables: { key },
      context: { headers: authHeaders },
      skip: !key,
      onCompleted: (d) => {
        // Pre-fill with existing answers
        const initial: AnswerMap = {};
        for (const a of d.matchingSurvey.existingAnswers) {
          initial[a.questionId] = a.valueJson;
        }
        setAnswers(initial);
        if (d.matchingSurvey.alreadySubmitted) {
          setSubmitted(true);
        }
      },
    },
  );

  const [submitResponse, { loading: submitting }] =
    useMutation<SubmitMatchingResponseResult>(SUBMIT_MATCHING_RESPONSE, {
      context: { headers: authHeaders },
      onCompleted: (d) => {
        if (d.submitMatchingResponse.ok) {
          setSubmitted(true);
        }
      },
    });

  const survey = data?.matchingSurvey;
  // Teammate questions are always optional regardless of the value stored in DB
  const ALWAYS_OPTIONAL_TYPES = new Set([
    MatchingQuestionType.PreferredTeammates,
    MatchingQuestionType.AvoidTeammates,
  ]);
  const questions = (survey?.questions ?? []).map((q) =>
    ALWAYS_OPTIONAL_TYPES.has(q.questionType as MatchingQuestionType)
      ? { ...q, required: false }
      : q,
  );
  const roster = survey?.roster ?? [];

  const answeredRequired = questions
    .filter((q) => q.required)
    .every((q) => {
      const a = answers[q.id];
      if (Array.isArray(a)) return a.length > 0;
      if (typeof a === "string") return a.trim().length > 0;
      return a != null;
    });

  // Compute preferred/avoid IDs for mutual exclusivity enforcement
  const preferredIds: string[] = [];
  const avoidIds: string[] = [];
  for (const q of questions) {
    const a = answers[q.id];
    if (!Array.isArray(a)) continue;
    if (q.questionType === MatchingQuestionType.PreferredTeammates) {
      preferredIds.push(...(a as string[]));
    } else if (q.questionType === MatchingQuestionType.AvoidTeammates) {
      avoidIds.push(...(a as string[]));
    }
  }
  const conflictIds = preferredIds.filter((id) => avoidIds.includes(id));
  const hasMutualExclusivityConflict = conflictIds.length > 0;

  const handleSubmit = () => {
    if (hasMutualExclusivityConflict) return;
    const answerList = Object.entries(answers).map(
      ([questionId, valueJson]) => ({
        questionId,
        valueJson,
      }),
    );
    submitResponse({ variables: { key, answers: answerList } });
  };

  const progress =
    questions.length > 0
      ? (Object.keys(answers).length / questions.length) * 100
      : 0;

  if (!key) {
    return (
      <ThinLayout title="Team Matching Survey">
        <Typography color="error" mt={4} align="center">
          Invalid survey link. Please use the link from your email.
        </Typography>
      </ThinLayout>
    );
  }

  if (loading) {
    return (
      <ThinLayout title="Team Matching Survey" loading>
        <Box />
      </ThinLayout>
    );
  }

  if (error || !survey) {
    return (
      <ThinLayout title="Team Matching Survey">
        <Typography color="error" mt={4} align="center">
          Survey not found or your link has expired. Please contact your
          instructor.
        </Typography>
      </ThinLayout>
    );
  }

  if (submitted) {
    return (
      <ThinLayout title="Team Matching Survey">
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          mt={8}
          gap={2}
        >
          <CheckCircle color="success" sx={{ fontSize: 72 }} />
          <Typography variant="h5" align="center">
            Your preferences have been submitted!
          </Typography>
          <Typography color="text.secondary" align="center">
            Your instructor will use these to form teams. You will be notified
            once teams are assigned.
          </Typography>
        </Box>
      </ThinLayout>
    );
  }

  return (
    <ThinLayout
      title={`Team Matching — ${survey.courseName}`}
      secondaryTitle={
        survey.dueDate
          ? `Due ${new Date(survey.dueDate).toLocaleDateString()}`
          : undefined
      }
    >
      <Box maxWidth={700} mx="auto" mt={3} px={2} pb={6}>
        {/* Instructor's message to students */}
        {survey.notes && (
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              mb: 3,
              borderLeft: "4px solid",
              borderColor: "primary.main",
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              mb={0.5}
            >
              Message from your instructor
            </Typography>
            <Typography variant="body2">{survey.notes}</Typography>
          </Paper>
        )}

        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{ mb: 3, borderRadius: 1, height: 6 }}
        />

        {questions.map((question) => {
          const isPreferred =
            question.questionType === MatchingQuestionType.PreferredTeammates;
          const isAvoid =
            question.questionType === MatchingQuestionType.AvoidTeammates;
          const questionExcludedIds = isPreferred
            ? avoidIds
            : isAvoid
              ? preferredIds
              : [];
          return (
            <QuestionCard
              key={question.id}
              question={question}
              answer={answers[question.id]}
              onAnswer={(v) =>
                setAnswers((prev) => ({ ...prev, [question.id]: v }))
              }
              roster={roster}
              excludedIds={questionExcludedIds}
            />
          );
        })}

        {hasMutualExclusivityConflict && (
          <Alert severity="error" sx={{ mb: 2 }}>
            The same classmate cannot appear in both your preferred and avoid
            lists. Please remove the conflict before submitting.
          </Alert>
        )}

        <Box display="flex" justifyContent="flex-end" mt={2}>
          <Button
            variant="contained"
            size="large"
            disabled={
              !answeredRequired || submitting || hasMutualExclusivityConflict
            }
            onClick={handleSubmit}
            startIcon={
              submitting ? (
                <CircularProgress aria-label="Loading" size={18} />
              ) : (
                <HourglassEmpty />
              )
            }
          >
            {submitting ? "Submitting…" : "Submit Preferences"}
          </Button>
        </Box>
      </Box>
    </ThinLayout>
  );
}
